import { trackEvent } from '@99designs/common/utils/platform';
import { AppState } from 'interactive/hooks/useAppState';
import industries from 'interactive/industries';
import logoTypes, { LogoType, LogoTypeKey } from 'interactive/logo-types';
import IndustrySelect from 'interactive/steps/IndustrySelect';
import IntentSelect from 'interactive/steps/IntentSelect';
import LogoTypesSelect from 'interactive/steps/LogoTypesSelect';
import NameInput from 'interactive/steps/NameInput';
import Title from 'interactive/Title';
import React from 'react';

interface TitleProps {
    step: number;
}

interface StepProps {
    step: number;
    categoryKey: string;
    currentState: AppState;
    onSave(state: AppState): AppState;
}

export function getConfig(variant: string) {
    switch (variant) {
        case 'v1':
            return defaultConfig;

        default:
            return defaultConfig;
    }
}

const COMPLETED_STEP_EVENT = 'Completed Prelaunch Interactive Step';

const defaultConfig = {
    maxSteps: 4,
    TitleComponent: ({ step }: TitleProps) => {
        switch (step) {
            case 1:
                return (
                    <Title
                        title="Let’s get started with logo design…"
                        subTitle="How can we help you?"
                    />
                );
            case 2:
                return (
                    <Title
                        title="Great, tell us a little more about your business…"
                        subTitle="What industry are you in?"
                    />
                );
            case 3:
                return (
                    <Title
                        title="The obvious (but important) question…"
                        subTitle="What name do you want in your logo?"
                    />
                );
            case 4:
                return (
                    <Title
                        title="Help designers learn more…"
                        subTitle="What type of logo are you after?"
                    />
                );

            default:
                return null;
        }
    },
    StepComponent: ({ categoryKey, currentState, onSave, step }: StepProps) => {
        const currentLogoTypes = currentState.logoTypes ? currentState.logoTypes : [];

        switch (step) {
            case 1:
                return (
                    <IntentSelect
                        onProceed={() => {
                            trackCompletedStep(currentState, categoryKey);
                            onSave({
                                ...currentState,
                                step: step + 1,
                            });
                        }}
                        category={categoryKey}
                    />
                );
            case 2:
                return (
                    <IndustrySelect
                        industries={industries}
                        currentValue={currentState.industry}
                        onChange={key => {
                            trackEvent(COMPLETED_STEP_EVENT, {
                                step_number: step,
                                step_name: 'business industry',
                                category: categoryKey,
                                value: key,
                            });

                            onSave({
                                ...currentState,
                                industry: key,
                                step: step + 1,
                            });
                        }}
                    />
                );
            case 3:
                return (
                    <NameInput
                        currentValue={currentState.name}
                        onChange={name => onSave({ ...currentState, name })}
                        onSubmit={() => {
                            trackCompletedStep(currentState, categoryKey);
                            onSave({
                                ...currentState,
                                step: step + 1,
                            });
                        }}
                    />
                );
            case 4:
                return (
                    <LogoTypesSelect
                        types={logoTypes as LogoType[]}
                        currentValue={currentLogoTypes}
                        onSelect={(type: LogoTypeKey) =>
                            onSave({
                                ...currentState,
                                logoTypes: currentLogoTypes.concat([type]),
                            })
                        }
                        onDeSelect={(type: LogoTypeKey) =>
                            onSave({
                                ...currentState,
                                logoTypes: currentLogoTypes.filter(t => t !== type),
                            })
                        }
                    />
                );

            default:
                return null;
        }
    },
};

export const trackCompletedStep = (state: AppState, categoryKey: string) => {
    const { step, name, industry, logoTypes } = state;

    switch (step) {
        case 1:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'intent',
                category: categoryKey,
            });
            break;
        case 2:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'business industry',
                category: categoryKey,
                value: industry,
            });
            break;
        case 3:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'business name',
                category: categoryKey,
                value: name,
            });
            break;
        case 4:
            trackEvent(COMPLETED_STEP_EVENT, {
                step_number: step,
                step_name: 'logo types',
                category: categoryKey,
                value: logoTypes ? logoTypes.join(',') : null,
            });
            break;
    }
};
